import { useEffect, useState, useRef, useMemo } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    Megaphone, PlusCircle, ArrowLeft, UploadSimple, Users, FileArrowDown,
    CheckCircle, X, CircleNotch, PaperPlaneTilt, Trash, Prohibit, Clock,
    PencilSimple, CopySimple, Trophy, Warning, Copy as CopyIcon,
    Funnel, ChatCircleDots,
} from "@phosphor-icons/react";

const FIELD_OPTIONS = [
    { value: "name", label: "Contact name" },
    { value: "company", label: "Company" },
    { value: "email", label: "Email" },
    { value: "phone", label: "Phone" },
];

const STATUS_BADGE = {
    draft: "bg-stone-100 text-stone-600",
    scheduled: "bg-violet-100 text-violet-700",
    sending: "bg-sky-100 text-sky-700",
    completed: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-amber-100 text-amber-700",
    failed: "bg-red-100 text-red-700",
};
const RECIP_BADGE = {
    queued: "bg-stone-100 text-stone-600", sent: "bg-sky-100 text-sky-700",
    delivered: "bg-indigo-100 text-indigo-700", read: "bg-emerald-100 text-emerald-700",
    failed: "bg-red-100 text-red-700", skipped: "bg-amber-100 text-amber-700",
};

// RFC-4180 CSV parser: handles quoted fields, embedded commas/newlines and "" escapes,
// so a value like "Kumar, Ravi" or "Acme, Inc." no longer shifts the phone column.
function parseCsvRows(text) {
    const t = (text || "").replace(/^\uFEFF/, ""); // strip BOM
    const rows = [];
    let field = "", row = [], inQuotes = false;
    for (let i = 0; i < t.length; i++) {
        const ch = t[i];
        if (inQuotes) {
            if (ch === '"') {
                if (t[i + 1] === '"') { field += '"'; i++; } else inQuotes = false;
            } else field += ch;
        } else if (ch === '"') {
            inQuotes = true;
        } else if (ch === ",") {
            row.push(field); field = "";
        } else if (ch === "\r") {
            // ignore; handled with \n
        } else if (ch === "\n") {
            row.push(field); rows.push(row); row = []; field = "";
        } else {
            field += ch;
        }
    }
    if (field.length || row.length) { row.push(field); rows.push(row); }
    return rows.filter((r) => r.some((c) => (c || "").trim() !== ""));
}

// Repair common spreadsheet phone artifacts (Excel leading ', = ; scientific notation).
function cleanPhone(v) {
    let s = (v || "").trim().replace(/^['=\s]+/, "").trim();
    if (/^[-+]?\d+(\.\d+)?e[-+]?\d+$/i.test(s)) {
        const n = Number(s);
        if (isFinite(n)) s = n.toLocaleString("fullwide", { useGrouping: false });
    }
    return s;
}

// Normalise a raw phone to the digits WhatsApp will receive (mirror of the backend
// _wa_num: strip non-digits + trunk zeros, auto-prepend 91 for bare 10-digit numbers).
function waNormalize(raw) {
    let d = cleanPhone(raw).replace(/\D/g, "").replace(/^0+/, "");
    if (d.length === 10) d = "91" + d;
    return d;
}

const HEADER_ALIASES = {
    phone: ["phone", "mobile", "phone number", "mobilephone", "contact number", "number", "whatsapp", "whatsapp number", "cell", "cellphone"],
    name: ["name", "full name", "contact name", "customer name", "first name"],
    company: ["company", "account", "company name", "organization", "organisation"],
    email: ["email", "email address", "e-mail"],
};

// Auto-detect which column maps to each field from the header row (-1 = not found).
function detectMapping(headers) {
    const norm = (headers || []).map((h) => (h || "").trim().toLowerCase());
    const find = (names) => norm.findIndex((h) => names.includes(h));
    return { phone: find(HEADER_ALIASES.phone), name: find(HEADER_ALIASES.name),
             company: find(HEADER_ALIASES.company), email: find(HEADER_ALIASES.email) };
}

// Build the recipient list from the parsed grid + chosen column mapping, with a
// pre-flight audit: valid, invalid (unusable numbers) and duplicate counts.
function analyzeCsv(grid, mapping) {
    const empty = { records: [], validCount: 0, invalidCount: 0, dupCount: 0, invalidSamples: [], preview: [] };
    if (!grid || grid.length < 2 || !mapping || mapping.phone < 0) return empty;
    const at = (cells, i) => (i >= 0 ? (cells[i] || "").trim() : "");
    const kept = [], invalidSamples = [], seen = new Set();
    let invalidCount = 0, dupCount = 0;
    for (let i = 1; i < grid.length; i++) {
        const cells = grid[i];
        const rawPhone = at(cells, mapping.phone);
        const norm = waNormalize(rawPhone);
        if (!norm || norm.length < 8) {
            invalidCount++;
            if (invalidSamples.length < 10) invalidSamples.push({ raw: rawPhone || "(empty)", name: at(cells, mapping.name) });
            continue;
        }
        if (seen.has(norm)) { dupCount++; continue; }
        seen.add(norm);
        kept.push({ name: at(cells, mapping.name), phone: cleanPhone(rawPhone),
                    company: at(cells, mapping.company), email: at(cells, mapping.email), _norm: norm });
    }
    return {
        records: kept.map(({ _norm, ...r }) => r),
        validCount: kept.length, invalidCount, dupCount, invalidSamples,
        preview: kept.slice(0, 5).map((r) => ({ name: r.name, phone: r._norm, company: r.company })),
    };
}

// ISO -> value for <input type="datetime-local"> in local time.
function toLocalInput(iso) {
    try {
        const d = new Date(iso);
        const p = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
    } catch { return ""; }
}

// Friendly absolute date for list rows (e.g. "12 Jun 2026, 14:32").
function fmtDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) +
            ", " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    } catch { return ""; }
}

const SOURCE_LABEL = {
    csv: "CSV upload", sf_contacts: "Salesforce Contacts", sf_leads: "Salesforce Leads",
    sf_listview: "Salesforce List View", sf_report: "Salesforce Report", sf_campaign: "Salesforce Campaign",
};


/* ---------------- Create wizard ---------------- */
function FilterSelect({ label, opts, val, onChange, tid }) {
    const items = (opts || []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
    return (
        <label className="text-xs text-stone2">{label}
            <select data-testid={`broadcast-filter-${tid}`} value={val || ""} onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm">
                <option value="">Any</option>
                {items.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </label>
    );
}

function PreviewButton({ onClick, previewing }) {
    return (
        <button onClick={onClick} disabled={previewing} data-testid="broadcast-sf-preview"
            className="inline-flex items-center gap-2 rounded-xl bg-ink px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
            {previewing ? <CircleNotch size={16} className="animate-spin" /> : <Users size={16} />} Preview audience
        </button>
    );
}

function TemplateFields({ templates, tplName, onTemplate, selectedTpl, bodyParams, setBodyParams, header, setHeader, tidPrefix }) {
    return (
        <>
            <select data-testid={`${tidPrefix}-template`} value={tplName} onChange={(e) => onTemplate(e.target.value)}
                className="mt-3 w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400">
                <option value="">Select a template…</option>
                {templates.map((t) => (
                    <option key={`${t.name}:${t.language}`} value={t.name} disabled={!t.usable}>
                        {t.name} · {t.language} {t.usable ? "" : `(${t.status})`}
                    </option>
                ))}
            </select>
            {selectedTpl && (
                <div className="mt-3 rounded-xl border border-stone-100 bg-stone-50 p-4 text-sm text-stone-700 whitespace-pre-wrap">
                    {selectedTpl.body_text || "(no body preview)"}
                </div>
            )}
            {selectedTpl?.header_format && (
                <div className="mt-3">
                    <label className="text-xs font-semibold text-stone-600">Header {selectedTpl.header_format} link (sent to everyone)</label>
                    <input data-testid={`${tidPrefix}-header-link`} value={header.link} onChange={(e) => setHeader((h) => ({ ...h, link: e.target.value }))}
                        placeholder="https://…/brochure.pdf"
                        className="mt-1 w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400" />
                    {selectedTpl.header_format === "document" && (
                        <input value={header.filename} onChange={(e) => setHeader((h) => ({ ...h, filename: e.target.value }))}
                            placeholder="Filename shown to customer (e.g. Alcazar-brochure.pdf)"
                            className="mt-2 w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400" />
                    )}
                </div>
            )}
            {bodyParams.length > 0 && (
                <div className="mt-4 space-y-3">
                    <div className="text-xs font-semibold text-stone-600">Fill the template variables</div>
                    {bodyParams.map((p, i) => (
                        <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`${tidPrefix}-var-${i + 1}`}>
                            <span className="rounded bg-stone-100 px-2 py-1 font-mono text-xs">{`{{${i + 1}}}`}</span>
                            <select value={p.source} onChange={(e) => setBodyParams((a) => a.map((x, j) => j === i ? { ...x, value: "", source: e.target.value } : x))}
                                className="rounded-lg border border-stone-200 px-3 py-2 text-sm">
                                <option value="static">Fixed text</option>
                                <option value="field">From contact field</option>
                            </select>
                            {p.source === "field" ? (
                                <select value={p.value} onChange={(e) => setBodyParams((a) => a.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
                                    className="flex-1 min-w-[160px] rounded-lg border border-stone-200 px-3 py-2 text-sm">
                                    <option value="">Choose field…</option>
                                    {FIELD_OPTIONS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                                </select>
                            ) : (
                                <input value={p.value} onChange={(e) => setBodyParams((a) => a.map((x, j) => j === i ? { ...x, value: e.target.value } : x))}
                                    placeholder="Type the value" className="flex-1 min-w-[160px] rounded-lg border border-stone-200 px-3 py-2 text-sm" />
                            )}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}

function CreateBroadcast({ onCancel, onCreated, initial, editing }) {
    const ds = initial?.data_source || {};
    const [name, setName] = useState(initial ? (editing ? (initial.name || "") : `${initial.name || "Broadcast"} (Copy)`) : "");
    const [sourceKind, setSourceKind] = useState(ds.kind || "csv");   // csv | sf_contacts | sf_leads | sf_listview | sf_report | sf_campaign
    const [csvRows, setCsvRows] = useState([]);
    const [csvName, setCsvName] = useState("");
    const [csvGrid, setCsvGrid] = useState([]);
    const [csvHeaders, setCsvHeaders] = useState([]);
    const [mapping, setMapping] = useState({ phone: -1, name: -1, company: -1, email: -1 });
    const [filters, setFilters] = useState(ds.filters || {});
    const [filterOpts, setFilterOpts] = useState(null);
    const [pickerSearch, setPickerSearch] = useState("");
    const [lvObjects, setLvObjects] = useState([]);
    const [lvObject, setLvObject] = useState(ds.list_view_object || "");
    const [listviews, setListviews] = useState([]);
    const [listViewId, setListViewId] = useState(ds.list_view_id || "");
    const [reports, setReports] = useState([]);
    const [reportId, setReportId] = useState(ds.report_id || "");
    const [phoneColumn, setPhoneColumn] = useState(ds.phone_column || "");
    const [sfCampaigns, setSfCampaigns] = useState([]);
    const [campaignId, setCampaignId] = useState(ds.sf_campaign_id || "");
    const [preview, setPreview] = useState(null);          // {total, sample}
    const [previewing, setPreviewing] = useState(false);
    const [templates, setTemplates] = useState(null);
    const [tplName, setTplName] = useState(initial?.template_name || "");
    const [bodyParams, setBodyParams] = useState(initial?.body_params || []);
    const [header, setHeader] = useState({ link: initial?.header?.link || "", filename: initial?.header?.filename || "" });
    // A/B test — split the audience 50/50 across two approved templates (Variant A = the fields above).
    const [abTest, setAbTest] = useState(!!initial?.ab_test);
    const vb = initial?.variant_b || {};
    const [tplNameB, setTplNameB] = useState(vb.template_name || "");
    const [bodyParamsB, setBodyParamsB] = useState(vb.body_params || []);
    const [headerB, setHeaderB] = useState({ link: vb.header?.link || "", filename: vb.header?.filename || "" });
    const [schedule, setSchedule] = useState(!!initial?.scheduled_at);
    const [scheduleAt, setScheduleAt] = useState(initial?.scheduled_at ? toLocalInput(initial.scheduled_at) : "");
    const [saving, setSaving] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        api.get("/whatsapp/broadcasts/templates")
            .then((r) => setTemplates(r.data.templates || []))
            .catch((e) => { setTemplates([]); toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't load templates"); });
    }, []);

    const selectedTpl = (templates || []).find((t) => t.name === tplName);
    const selectedTplB = (templates || []).find((t) => t.name === tplNameB);

    const onTemplate = (nm) => {
        setTplName(nm);
        const t = (templates || []).find((x) => x.name === nm);
        const n = t?.variable_count || 0;
        setBodyParams(Array.from({ length: n }, () => ({ source: "static", value: "" })));
        setHeader({ link: "", filename: "" });
    };

    const onTemplateB = (nm) => {
        setTplNameB(nm);
        const t = (templates || []).find((x) => x.name === nm);
        const n = t?.variable_count || 0;
        setBodyParamsB(Array.from({ length: n }, () => ({ source: "static", value: "" })));
        setHeaderB({ link: "", filename: "" });
    };

    const onCsv = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = () => {
            const grid = parseCsvRows(reader.result);
            if (!grid.length) { toast.error("That file looks empty."); return; }
            const headers = grid[0] || [];
            const m = detectMapping(headers);
            if (m.phone < 0) m.phone = 0;  // no recognizable header → assume the first column is the phone
            setCsvGrid(grid); setCsvHeaders(headers); setMapping(m); setCsvName(f.name);
        };
        reader.readAsText(f);
        e.target.value = "";  // allow re-uploading the same file
    };

    // Recompute recipients + audit whenever the grid or the column mapping changes.
    const csvAnalysis = useMemo(() => analyzeCsv(csvGrid, mapping), [csvGrid, mapping]);
    useEffect(() => { setCsvRows(csvAnalysis.records); }, [csvAnalysis]);

    const buildDataSource = () => {
        if (sourceKind === "sf_contacts" || sourceKind === "sf_leads") return { kind: sourceKind, filters };
        if (sourceKind === "sf_listview") {
            const lv = listviews.find((l) => l.id === listViewId);
            return { kind: "sf_listview", list_view_id: listViewId, list_view_object: lv?.object || lvObject };
        }
        if (sourceKind === "sf_report") return { kind: "sf_report", report_id: reportId, phone_column: phoneColumn || null };
        if (sourceKind === "sf_campaign") return { kind: "sf_campaign", sf_campaign_id: campaignId };
        return { kind: "csv" };
    };

    // Load the picker/filter data for the chosen Salesforce source.
    // On the first render (edit/clone prefill) we load option lists but keep the prefilled selections.
    const firstRun = useRef(true);
    useEffect(() => {
        if (!firstRun.current) setPreview(null);
        if (sourceKind === "sf_contacts" || sourceKind === "sf_leads") {
            if (!firstRun.current) { setFilterOpts(null); setFilters({}); }
            api.get(`/whatsapp/broadcasts/sf-filter-options?kind=${sourceKind}`).then((r) => setFilterOpts(r.data)).catch(() => setFilterOpts({}));
        } else if (sourceKind === "sf_listview") {
            if (!firstRun.current) { setListViewId(""); setLvObject(""); }
            api.get("/whatsapp/broadcasts/sf-listview-objects").then((r) => setLvObjects(r.data.objects || [])).catch(() => setLvObjects([]));
        } else if (sourceKind === "sf_report") {
            if (!firstRun.current) setReportId("");
            api.get("/whatsapp/broadcasts/sf-reports").then((r) => setReports(r.data.reports || [])).catch(() => setReports([]));
        } else if (sourceKind === "sf_campaign") {
            if (!firstRun.current) setCampaignId("");
            api.get("/whatsapp/broadcasts/sf-campaigns").then((r) => setSfCampaigns(r.data.campaigns || [])).catch(() => setSfCampaigns([]));
        }
        firstRun.current = false;
    }, [sourceKind]);

    // When a List View object is chosen, load its list views.
    useEffect(() => {
        if (sourceKind === "sf_listview" && lvObject)
            api.get(`/whatsapp/broadcasts/sf-listviews?sobject=${encodeURIComponent(lvObject)}`).then((r) => setListviews(r.data.listviews || [])).catch(() => setListviews([]));
    }, [lvObject, sourceKind]);

    const runPreview = async () => {
        setPreviewing(true); setPreview(null);
        try {
            const { data } = await api.post("/whatsapp/broadcasts/preview-source", { data_source: buildDataSource(), records: [] });
            setPreview(data);
            if (!data.total) toast.info("No contacts with a phone number matched.");
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Preview failed");
        } finally { setPreviewing(false); }
    };

    const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v || undefined }));
    const sfReady = {
        sf_contacts: true, sf_leads: true,
        sf_listview: !!listViewId, sf_report: !!reportId, sf_campaign: !!campaignId,
    };
    const audienceReady = editing ? true : (sourceKind === "csv" ? csvRows.length > 0 : (preview && preview.total > 0));

    const create = async () => {
        if (!name.trim()) return toast.error("Give the broadcast a name.");
        if (!tplName) return toast.error("Pick an approved template.");
        if (abTest && !tplNameB) return toast.error("Pick an approved template for Variant B.");
        if (!audienceReady) return toast.error("Load an audience first.");
        if (schedule && !scheduleAt) return toast.error("Pick a date & time to schedule.");
        setSaving(true);
        try {
            const payload = {
                name: name.trim(), template_name: tplName, template_language: selectedTpl?.language || "en_US",
                body_params: bodyParams,
                header: selectedTpl?.header_format && header.link
                    ? { type: selectedTpl.header_format, link: header.link, filename: header.filename || null } : null,
                data_source: buildDataSource(),
                records: sourceKind === "csv" ? csvRows : [],
                scheduled_at: schedule && scheduleAt ? new Date(scheduleAt).toISOString() : null,
                ab_test: abTest,
                variant_b: abTest ? {
                    template_name: tplNameB, template_language: selectedTplB?.language || "en_US",
                    body_params: bodyParamsB,
                    header: selectedTplB?.header_format && headerB.link
                        ? { type: selectedTplB.header_format, link: headerB.link, filename: headerB.filename || null } : null,
                } : null,
            };
            const { data } = editing
                ? await api.put(`/whatsapp/broadcasts/${initial.id}`, payload)
                : await api.post("/whatsapp/broadcasts", payload);
            toast.success(editing ? "Broadcast updated" : (data.status === "scheduled" ? "Broadcast scheduled" : "Broadcast created"));
            onCreated(data.id);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Could not save broadcast");
        } finally { setSaving(false); }
    };

    return (
        <div data-testid="broadcast-create" className="space-y-6">
            <button data-testid="broadcast-back" onClick={onCancel} className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone2 hover:text-ink">
                <ArrowLeft size={16} /> Back to broadcasts
            </button>

            {/* Name */}
            <section className="rounded-2xl border border-stone-200 bg-white p-6">
                <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Broadcast name</label>
                <input data-testid="broadcast-name" value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Diwali offer — Alcazar prospects"
                    className="mt-2 w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400" />
            </section>

            {/* Audience */}
            <section className="rounded-2xl border border-stone-200 bg-white p-6">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                    <Users size={14} weight="duotone" /> Audience
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    {[["csv", "CSV upload"], ["sf_contacts", "SF Contacts"], ["sf_leads", "SF Leads"],
                      ["sf_listview", "SF List View"], ["sf_report", "SF Report"], ["sf_campaign", "SF Campaign"]].map(([k, l]) => (
                        <button key={k} data-testid={`broadcast-source-${k}`} onClick={() => setSourceKind(k)}
                            className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${sourceKind === k ? "bg-emerald-500 text-white" : "border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>
                            {l}
                        </button>
                    ))}
                </div>

                {sourceKind === "csv" && (
                    <div className="mt-4">
                        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onCsv} className="hidden" data-testid="broadcast-csv-input" />
                        <button onClick={() => fileRef.current?.click()} data-testid="broadcast-csv-btn"
                            className="inline-flex items-center gap-2 rounded-xl border border-dashed border-stone-300 px-4 py-3 text-sm font-semibold text-stone-600 hover:border-emerald-400">
                            <UploadSimple size={16} /> {csvName || "Upload a CSV (needs a 'phone' column)"}
                        </button>
                        <p className="mt-2 text-xs text-stone2">Recognised columns: phone/mobile (required), name, company, email. Rows with a comma inside a value are handled automatically.</p>
                        {editing && !csvGrid.length && (
                            <p className="mt-1 text-xs text-amber-600" data-testid="broadcast-edit-csv-note">Editing: the current recipient list is kept unless you upload a new CSV.</p>
                        )}

                        {csvGrid.length > 0 && (
                            <div className="mt-4 space-y-4" data-testid="broadcast-csv-mapper">
                                {/* Column mapper */}
                                <div className="rounded-xl border border-stone-200 bg-white p-4">
                                    <div className="text-[11px] font-bold uppercase tracking-widest text-stone2">Map your columns</div>
                                    <p className="mt-1 text-xs text-stone2">We detected these from <b>{csvName}</b> ({csvGrid.length - 1} data rows). Adjust if a column is wrong.</p>
                                    <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                        {[["phone", "Phone", true], ["name", "Name", false], ["company", "Company", false], ["email", "Email", false]].map(([key, label, req]) => (
                                            <label key={key} className="text-xs font-semibold text-stone-600">
                                                {label}{req && <span className="text-red-500"> *</span>}
                                                <select data-testid={`broadcast-map-${key}`} value={mapping[key]}
                                                    onChange={(e) => setMapping((m) => ({ ...m, [key]: Number(e.target.value) }))}
                                                    className="mt-1 w-full rounded-lg border border-stone-200 px-2 py-2 text-sm outline-none focus:border-emerald-400">
                                                    {!req && <option value={-1}>— none —</option>}
                                                    {csvHeaders.map((h, i) => (
                                                        <option key={i} value={i}>{(h || "").trim() || `Column ${i + 1}`}</option>
                                                    ))}
                                                </select>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Audience pre-check */}
                                <div className="flex flex-wrap items-center gap-2" data-testid="broadcast-csv-precheck">
                                    <span data-testid="precheck-valid" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-700">
                                        <CheckCircle size={14} weight="fill" /> {csvAnalysis.validCount} valid
                                    </span>
                                    {csvAnalysis.invalidCount > 0 && (
                                        <span data-testid="precheck-invalid" className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-700">
                                            <Warning size={14} weight="fill" /> {csvAnalysis.invalidCount} invalid
                                        </span>
                                    )}
                                    {csvAnalysis.dupCount > 0 && (
                                        <span data-testid="precheck-dupes" className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700">
                                            <CopyIcon size={14} weight="fill" /> {csvAnalysis.dupCount} duplicate{csvAnalysis.dupCount === 1 ? "" : "s"} removed
                                        </span>
                                    )}
                                </div>

                                {csvAnalysis.invalidCount > 0 && (
                                    <div data-testid="broadcast-csv-invalid-samples" className="rounded-xl border border-red-100 bg-red-50/60 p-3">
                                        <div className="text-xs font-semibold text-red-700">These rows will be skipped (unusable number):</div>
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {csvAnalysis.invalidSamples.map((s, i) => (
                                                <span key={i} className="rounded-full bg-white px-3 py-1 text-xs text-stone-600 shadow-sm">
                                                    {s.name || "—"} · <span className="font-mono">{s.raw}</span>
                                                </span>
                                            ))}
                                            {csvAnalysis.invalidCount > csvAnalysis.invalidSamples.length && (
                                                <span className="px-1 py-1 text-xs text-red-600">+{csvAnalysis.invalidCount - csvAnalysis.invalidSamples.length} more</span>
                                            )}
                                        </div>
                                        <p className="mt-2 text-[11px] text-red-600/80">Check the column mapping above, or that these numbers include the country code.</p>
                                    </div>
                                )}

                                {/* Preview of what will be sent */}
                                {csvAnalysis.preview.length > 0 && (
                                    <div data-testid="broadcast-csv-preview" className="overflow-hidden rounded-xl border border-stone-200">
                                        <div className="border-b border-stone-100 bg-stone-50 px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                                            Preview — first {csvAnalysis.preview.length} of {csvAnalysis.validCount} recipients
                                        </div>
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="text-left text-[11px] uppercase tracking-wider text-stone2">
                                                    <th className="px-4 py-2 font-semibold">Name</th>
                                                    <th className="px-4 py-2 font-semibold">Number (as sent)</th>
                                                    <th className="px-4 py-2 font-semibold">Company</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {csvAnalysis.preview.map((r, i) => (
                                                    <tr key={i} className="border-t border-stone-100">
                                                        <td className="px-4 py-2">{r.name || "—"}</td>
                                                        <td className="px-4 py-2 font-mono text-xs">{r.phone}</td>
                                                        <td className="px-4 py-2 text-stone2">{r.company || "—"}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {(sourceKind === "sf_contacts" || sourceKind === "sf_leads") && (
                    <div className="mt-4 space-y-3" data-testid="broadcast-filters">
                        <input value={filters.search || ""} onChange={(e) => setF("search", e.target.value)} placeholder="Search name / company / email"
                            className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400" data-testid="broadcast-filter-search" />
                        <div className="grid gap-3 sm:grid-cols-3">
                            {sourceKind === "sf_leads" && <FilterSelect label="Status" opts={filterOpts?.status} val={filters.status} onChange={(v) => setF("status", v)} tid="status" />}
                            <FilterSelect label="Industry" opts={filterOpts?.industry} val={filters.industry} onChange={(v) => setF("industry", v)} tid="industry" />
                            <FilterSelect label="State" opts={filterOpts?.state} val={filters.state} onChange={(v) => setF("state", v)} tid="state" />
                            <FilterSelect label="Lead source" opts={filterOpts?.lead_source} val={filters.lead_source} onChange={(v) => setF("lead_source", v)} tid="lead_source" />
                            {sourceKind === "sf_leads" && <FilterSelect label="Rating" opts={filterOpts?.rating} val={filters.rating} onChange={(v) => setF("rating", v)} tid="rating" />}
                            <FilterSelect label="Owner" opts={(filterOpts?.owners || []).map((o) => ({ value: o.id, label: o.name }))} val={filters.owner_id} onChange={(v) => setF("owner_id", v)} tid="owner" />
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2">
                            <label className="text-xs text-stone2">Created after
                                <input type="date" value={filters.created_after || ""} onChange={(e) => setF("created_after", e.target.value)}
                                    className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm" data-testid="broadcast-filter-created-after" /></label>
                            <label className="text-xs text-stone2">Created before
                                <input type="date" value={filters.created_before || ""} onChange={(e) => setF("created_before", e.target.value)}
                                    className="mt-1 w-full rounded-xl border border-stone-200 px-3 py-2 text-sm" /></label>
                        </div>
                        <PreviewButton onClick={runPreview} previewing={previewing} />
                    </div>
                )}

                {sourceKind === "sf_listview" && (
                    <div className="mt-4 space-y-3">
                        <select data-testid="broadcast-lv-object" value={lvObject} onChange={(e) => setLvObject(e.target.value)}
                            className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm">
                            <option value="">Choose object…</option>
                            {lvObjects.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                        {lvObject && (
                            <select data-testid="broadcast-lv-view" value={listViewId} onChange={(e) => { setListViewId(e.target.value); setPreview(null); }}
                                className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm">
                                <option value="">Choose a list view…</option>
                                {listviews.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                        )}
                        {listViewId && <PreviewButton onClick={runPreview} previewing={previewing} />}
                    </div>
                )}

                {sourceKind === "sf_report" && (
                    <div className="mt-4 space-y-3">
                        <select data-testid="broadcast-report" value={reportId} onChange={(e) => { setReportId(e.target.value); setPreview(null); }}
                            className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm">
                            <option value="">Choose a tabular report…</option>
                            {reports.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                        <input value={phoneColumn} onChange={(e) => setPhoneColumn(e.target.value)} placeholder="Phone column name in the report (optional — auto-detected)"
                            className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400" data-testid="broadcast-report-phone-col" />
                        {reportId && <PreviewButton onClick={runPreview} previewing={previewing} />}
                    </div>
                )}

                {sourceKind === "sf_campaign" && (
                    <div className="mt-4 space-y-3">
                        <select data-testid="broadcast-campaign" value={campaignId} onChange={(e) => { setCampaignId(e.target.value); setPreview(null); }}
                            className="w-full rounded-xl border border-stone-200 px-4 py-2.5 text-sm">
                            <option value="">Choose a campaign…</option>
                            {sfCampaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                        {campaignId && <PreviewButton onClick={runPreview} previewing={previewing} />}
                    </div>
                )}

                {preview && (
                    <div data-testid="broadcast-preview" className="mt-4 rounded-xl border border-stone-100 bg-stone-50 p-4">
                        <div className="text-sm font-bold">{preview.total} recipient{preview.total === 1 ? "" : "s"} {preview.capped ? "(capped at 500)" : ""}</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                            {(preview.sample || []).map((r, i) => (
                                <span key={i} className="rounded-full bg-white px-3 py-1 text-xs text-stone-600 shadow-sm">
                                    {r.name || "—"} · {r.phone}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </section>

            {/* Template */}
            <section className="rounded-2xl border border-stone-200 bg-white p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                        <Megaphone size={14} weight="duotone" /> {abTest ? "A/B test templates" : "Approved template"}
                    </div>
                    <label className="flex cursor-pointer items-center gap-2 text-xs font-semibold text-stone-600">
                        <input type="checkbox" data-testid="broadcast-abtest-toggle" checked={abTest} onChange={(e) => setAbTest(e.target.checked)} />
                        A/B test
                    </label>
                </div>
                {abTest && (
                    <p className="mt-1 text-xs text-stone2">Your audience is split 50/50 — half receive Variant A, half receive Variant B. Compare reply rates on the broadcast page and pick a winner.</p>
                )}
                {templates === null ? (
                    <p className="mt-3 text-sm text-stone2"><CircleNotch size={14} className="mr-1 inline animate-spin" /> Loading templates…</p>
                ) : templates.length === 0 ? (
                    <p className="mt-3 text-sm text-stone2">No templates found. Create &amp; get one approved in the <b>Templates</b> tab (or WhatsApp Manager) first.</p>
                ) : abTest ? (
                    <div className="mt-4 grid gap-4 lg:grid-cols-2">
                        <div data-testid="broadcast-variant-a" className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                            <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">Variant A</div>
                            <TemplateFields templates={templates} tplName={tplName} onTemplate={onTemplate} selectedTpl={selectedTpl}
                                bodyParams={bodyParams} setBodyParams={setBodyParams} header={header} setHeader={setHeader} tidPrefix="broadcast" />
                        </div>
                        <div data-testid="broadcast-variant-b" className="rounded-xl border border-sky-200 bg-sky-50/40 p-4">
                            <div className="text-xs font-bold uppercase tracking-widest text-sky-700">Variant B</div>
                            <TemplateFields templates={templates} tplName={tplNameB} onTemplate={onTemplateB} selectedTpl={selectedTplB}
                                bodyParams={bodyParamsB} setBodyParams={setBodyParamsB} header={headerB} setHeader={setHeaderB} tidPrefix="broadcast-b" />
                        </div>
                    </div>
                ) : (
                    <TemplateFields templates={templates} tplName={tplName} onTemplate={onTemplate} selectedTpl={selectedTpl}
                        bodyParams={bodyParams} setBodyParams={setBodyParams} header={header} setHeader={setHeader} tidPrefix="broadcast" />
                )}
            </section>

            {/* When to send */}
            <section className="rounded-2xl border border-stone-200 bg-white p-6" data-testid="broadcast-schedule">
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                    <Clock size={14} weight="duotone" /> When to send
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                    <button data-testid="broadcast-when-now" onClick={() => setSchedule(false)}
                        className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${!schedule ? "bg-emerald-500 text-white" : "border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>
                        Create as draft (send manually)
                    </button>
                    <button data-testid="broadcast-when-schedule" onClick={() => setSchedule(true)}
                        className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${schedule ? "bg-emerald-500 text-white" : "border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>
                        Schedule for later
                    </button>
                </div>
                {schedule && (
                    <input type="datetime-local" data-testid="broadcast-schedule-at" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)}
                        className="mt-3 rounded-xl border border-stone-200 px-4 py-2.5 text-sm outline-none focus:border-emerald-400" />
                )}
            </section>

            <div className="flex justify-end gap-3">
                <button onClick={onCancel} className="rounded-full border border-stone-200 px-5 py-2.5 text-sm font-semibold text-stone-600 hover:bg-stone-50">Cancel</button>
                <button data-testid="broadcast-create-submit" onClick={create} disabled={saving}
                    className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-50">
                    {saving ? <CircleNotch size={16} className="animate-spin" /> : <CheckCircle size={16} weight="fill" />} {schedule ? "Schedule broadcast" : (editing ? "Save changes" : "Create broadcast")}
                </button>
            </div>
        </div>
    );
}

/* ---------------- Detail ---------------- */
function StatCard({ label, value, testid, cls }) {
    return (
        <div className="rounded-xl border border-stone-100 bg-white p-4 text-center" data-testid={testid}>
            <div className={`font-heading text-2xl font-bold ${cls || ""}`}>{value}</div>
            <div className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-stone2">{label}</div>
        </div>
    );
}

function BroadcastDetail({ id, onBack, onChanged, onEdit }) {
    const [b, setB] = useState(null);
    const [busy, setBusy] = useState(false);
    const timer = useRef(null);

    const load = async () => {
        try { const { data } = await api.get(`/whatsapp/broadcasts/${id}`); setB(data); }
        catch (e) { toast.error("Couldn't load broadcast"); }
    };
    useEffect(() => { load(); return () => clearInterval(timer.current); }, [id]);
    useEffect(() => {
        clearInterval(timer.current);
        if (b?.status === "sending") timer.current = setInterval(load, 3000);
        return () => clearInterval(timer.current);
    }, [b?.status]);

    const send = async () => {
        setBusy(true);
        try { await api.post(`/whatsapp/broadcasts/${id}/send`); toast.success("Sending started"); await load(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not start"); }
        finally { setBusy(false); }
    };
    const cancel = async () => {
        setBusy(true);
        try { await api.post(`/whatsapp/broadcasts/${id}/cancel`); toast.success("Cancelled"); await load(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not cancel"); }
        finally { setBusy(false); }
    };
    const del = async () => {
        if (!window.confirm("Delete this broadcast and its recipient records?")) return;
        try { await api.delete(`/whatsapp/broadcasts/${id}`); toast.success("Deleted"); onBack(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not delete"); }
    };
    const declareWinner = async (variant) => {
        setBusy(true);
        try { await api.post(`/whatsapp/broadcasts/${id}/winner`, { variant }); toast.success(`Variant ${variant} marked as the winner`); await load(); onChanged?.(); }
        catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not set winner"); }
        finally { setBusy(false); }
    };

    if (!b) return <p className="text-sm text-stone2"><CircleNotch size={14} className="mr-1 inline animate-spin" /> Loading…</p>;
    const c = b.counts || {};
    return (
        <div data-testid="broadcast-detail" className="space-y-6">
            <button data-testid="broadcast-detail-back" onClick={onBack} className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone2 hover:text-ink">
                <ArrowLeft size={16} /> Back to broadcasts
            </button>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="font-heading text-2xl font-bold">{b.name}</h2>
                    <div className="mt-1 flex items-center gap-2 text-sm text-stone2">
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_BADGE[b.status] || ""}`} data-testid="broadcast-detail-status">{b.status}</span>
                        {b.ab_test && <span data-testid="broadcast-detail-ab-badge" className="rounded-full bg-violet-100 px-2 py-0.5 text-xs font-bold text-violet-700">A/B</span>}
                        <span>Template: <b>{b.template_name}</b></span>
                        {b.status === "scheduled" && b.scheduled_at && (
                            <span className="inline-flex items-center gap-1 text-violet-700"><Clock size={13} /> {new Date(b.scheduled_at).toLocaleString()}</span>
                        )}
                    </div>
                </div>
                <div className="flex gap-2">
                    {(b.status === "draft" || b.status === "scheduled") && (
                        <button data-testid="broadcast-detail-edit" onClick={() => onEdit?.(b)}
                            className="inline-flex items-center gap-2 rounded-full border border-stone-200 px-4 py-2.5 text-sm font-semibold text-stone-600 hover:bg-stone-50">
                            <PencilSimple size={16} /> Edit
                        </button>
                    )}
                    {(b.status === "draft" || b.status === "scheduled") && (
                        <button data-testid="broadcast-send" onClick={send} disabled={busy}
                            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600 disabled:opacity-50">
                            {busy ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} weight="fill" />} Send now
                        </button>
                    )}
                    {(b.status === "sending" || b.status === "scheduled") && (
                        <button data-testid="broadcast-cancel" onClick={cancel} disabled={busy}
                            className="inline-flex items-center gap-2 rounded-full border border-amber-300 px-5 py-2.5 text-sm font-semibold text-amber-700 hover:bg-amber-50 disabled:opacity-50">
                            <Prohibit size={16} /> {b.status === "scheduled" ? "Unschedule" : "Cancel"}
                        </button>
                    )}
                    <a href={`${API_BASE}/whatsapp/broadcasts/${id}/recipients.csv`} data-testid="broadcast-export"
                        className="inline-flex items-center gap-2 rounded-full border border-stone-200 px-4 py-2.5 text-sm font-semibold text-stone-600 hover:bg-stone-50">
                        <FileArrowDown size={16} /> Export
                    </a>
                    {b.status !== "sending" && (
                        <button data-testid="broadcast-delete" onClick={del}
                            className="inline-flex items-center gap-2 rounded-full border border-red-200 px-4 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50">
                            <Trash size={16} />
                        </button>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8">
                <StatCard label="Total" value={c.total || 0} testid="stat-total" />
                <StatCard label="Queued" value={c.queued || 0} testid="stat-queued" />
                <StatCard label="Sent" value={c.sent || 0} testid="stat-sent" cls="text-sky-600" />
                <StatCard label="Delivered" value={c.delivered || 0} testid="stat-delivered" cls="text-indigo-600" />
                <StatCard label="Read" value={c.read || 0} testid="stat-read" cls="text-emerald-600" />
                <StatCard label="Replied" value={c.replied || 0} testid="stat-replied" cls="text-emerald-600" />
                <StatCard label="Failed" value={c.failed || 0} testid="stat-failed" cls="text-red-500" />
                <StatCard label="Skipped" value={c.skipped || 0} testid="stat-skipped" cls="text-stone-500" />
            </div>

            {(() => {
                const f = b.funnel || {};
                const base = f.sent || f.total || 0;
                const stages = [
                    { key: "sent", label: "Sent", value: f.sent || 0, color: "bg-sky-500" },
                    { key: "delivered", label: "Delivered", value: f.delivered || 0, color: "bg-indigo-500", rate: f.delivery_rate },
                    { key: "read", label: "Read", value: f.read || 0, color: "bg-emerald-500", rate: f.read_rate },
                    { key: "replied", label: "Replied", value: f.replied || 0, color: "bg-teal-500", rate: f.reply_rate },
                ];
                const replies = b.recent_replies || [];
                return (
                    <div className="grid gap-4 lg:grid-cols-5">
                        {/* Delivery funnel */}
                        <div data-testid="broadcast-funnel" className="rounded-2xl border border-stone-200 bg-white p-6 lg:col-span-3">
                            <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                                <Funnel size={14} weight="duotone" /> Delivery funnel
                            </div>
                            <div className="mt-4 space-y-3">
                                {stages.map((s) => {
                                    const pct = base ? Math.round((s.value / base) * 100) : 0;
                                    return (
                                        <div key={s.key} data-testid={`funnel-${s.key}`}>
                                            <div className="flex items-center justify-between text-xs">
                                                <span className="font-semibold text-stone-600">{s.label}</span>
                                                <span className="text-stone2"><b className="text-ink">{s.value}</b>{s.rate != null ? ` · ${s.rate}%` : ` · ${pct}%`}</span>
                                            </div>
                                            <div className="mt-1 h-2.5 w-full overflow-hidden rounded-full bg-stone-100">
                                                <div className={`h-full rounded-full ${s.color} transition-all duration-500`} style={{ width: `${Math.max(pct, s.value > 0 ? 3 : 0)}%` }} />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2 border-t border-stone-100 pt-3 text-xs">
                                <span className="rounded-full bg-red-50 px-2.5 py-1 font-semibold text-red-600">{f.failed || 0} failed</span>
                                <span data-testid="funnel-skipped" className="rounded-full bg-stone-100 px-2.5 py-1 font-semibold text-stone-600" title="Contacts skipped because they opted out (replied STOP)">
                                    {f.skipped || 0} skipped (opted out)
                                </span>
                            </div>
                        </div>

                        {/* Reply insights */}
                        <div data-testid="broadcast-replies" className="rounded-2xl border border-stone-200 bg-white p-6 lg:col-span-2">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                                    <ChatCircleDots size={14} weight="duotone" /> Reply insights
                                </div>
                                <span className="rounded-full bg-teal-50 px-2.5 py-1 text-xs font-bold text-teal-700">{f.reply_rate ?? 0}% reply rate</span>
                            </div>
                            {replies.length === 0 ? (
                                <p className="mt-4 text-sm text-stone2">No replies yet. Replies land here (and open the 24h chat window).</p>
                            ) : (
                                <div className="mt-3 max-h-64 space-y-2 overflow-y-auto pr-1">
                                    {replies.map((r, i) => (
                                        <div key={i} data-testid={`reply-item-${i}`} className="rounded-xl border border-stone-100 bg-stone-50/60 px-3 py-2">
                                            <div className="flex items-center justify-between text-xs">
                                                <span className="font-semibold text-stone-700">{r.name || r.phone}</span>
                                                <span className="text-stone-400">{r.at ? new Date(r.at).toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                                            </div>
                                            {r.text ? <p className="mt-1 text-sm text-stone-600">{r.text}</p>
                                                : <p className="mt-1 text-sm italic text-stone-400">(media or no text)</p>}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}

            {b.ab_test && (
                <div data-testid="broadcast-ab-comparison" className="rounded-2xl border border-stone-200 bg-white p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone2">
                            <Trophy size={14} weight="duotone" /> A/B test results
                        </div>
                        {b.ab_winner && (
                            <span data-testid="broadcast-ab-winner-badge" className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-700">
                                Winner: Variant {b.ab_winner}
                            </span>
                        )}
                    </div>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                        {["A", "B"].map((v) => {
                            const vc = (b.variant_counts || {})[v] || {};
                            const tplNm = v === "A" ? b.template_name : b.variant_b?.template_name;
                            const isWinner = b.ab_winner === v;
                            const accentText = v === "A" ? "text-emerald-700" : "text-sky-700";
                            const borderCls = isWinner ? "border-amber-300 ring-2 ring-amber-200" : (v === "A" ? "border-emerald-200" : "border-sky-200");
                            return (
                                <div key={v} data-testid={`broadcast-ab-${v}`} className={`rounded-xl border p-4 ${borderCls}`}>
                                    <div className="flex items-center justify-between">
                                        <div className={`text-xs font-bold uppercase tracking-widest ${accentText}`}>Variant {v}</div>
                                        {isWinner && <Trophy size={16} weight="fill" className="text-amber-500" />}
                                    </div>
                                    <div className="mt-1 truncate text-sm font-semibold">{tplNm || "—"}</div>
                                    <div className="mt-3 flex items-baseline gap-1">
                                        <span data-testid={`broadcast-ab-${v}-replyrate`} className="font-heading text-3xl font-bold">{vc.reply_rate ?? 0}%</span>
                                        <span className="text-xs text-stone2">reply rate</span>
                                    </div>
                                    <div className="mt-2 grid grid-cols-5 gap-2 text-center text-xs">
                                        <div><div className="font-bold text-sky-600">{vc.sent || 0}</div><div className="text-stone2">sent</div></div>
                                        <div><div className="font-bold text-indigo-600">{vc.delivered || 0}</div><div className="text-stone2">deliv.</div></div>
                                        <div><div className="font-bold text-emerald-600">{vc.read || 0}</div><div className="text-stone2">read</div></div>
                                        <div><div className="font-bold text-emerald-600">{vc.replied || 0}</div><div className="text-stone2">replied</div></div>
                                        <div><div data-testid={`broadcast-ab-${v}-failed`} className="font-bold text-red-500">{vc.failed || 0}</div><div className="text-stone2">failed</div></div>
                                    </div>
                                    {(b.status === "sending" || b.status === "completed") && (
                                        <button data-testid={`broadcast-ab-winner-${v}`} onClick={() => declareWinner(v)} disabled={busy}
                                            className={`mt-3 w-full rounded-full px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${isWinner ? "bg-amber-500 text-white" : "border border-stone-200 text-stone-600 hover:bg-stone-50"}`}>
                                            {isWinner ? "Winner" : "Mark as winner"}
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
                <table className="w-full text-sm">
                    <thead className="bg-stone-50 text-left text-[11px] font-bold uppercase tracking-wider text-stone2">
                        <tr><th className="px-4 py-3">Name</th><th className="px-4 py-3">Phone</th><th className="px-4 py-3">Company</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Note</th></tr>
                    </thead>
                    <tbody data-testid="broadcast-recipients">
                        {(b.recipients || []).map((r) => (
                            <tr key={r.id} className="border-t border-stone-100">
                                <td className="px-4 py-2.5">{r.name || "—"}</td>
                                <td className="px-4 py-2.5 font-mono text-xs">{r.phone}</td>
                                <td className="px-4 py-2.5 text-stone2">{r.company || "—"}</td>
                                <td className="px-4 py-2.5">
                                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${RECIP_BADGE[r.status] || ""}`}>{r.status}</span>
                                    {r.replied_at && <span className="ml-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">replied</span>}
                                </td>
                                <td className="px-4 py-2.5 text-xs text-stone2" title={r.error_raw || ""}>{r.error || (r.sent_at ? "sent" : "")}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/* ---------------- Root ---------------- */
export default function WhatsAppBroadcasts() {
    const [view, setView] = useState("list");   // list | create | detail
    const [items, setItems] = useState(null);
    const [detailId, setDetailId] = useState(null);
    const [formInitial, setFormInitial] = useState(null);   // prefill source for clone/edit
    const [formEditing, setFormEditing] = useState(false);  // true = edit existing, false = create/clone

    const load = async () => {
        try { const { data } = await api.get("/whatsapp/broadcasts"); setItems(data.broadcasts || []); }
        catch (e) { setItems([]); toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't load broadcasts"); }
    };
    useEffect(() => { if (view === "list") load(); }, [view]);

    const openNew = () => { setFormInitial(null); setFormEditing(false); setView("create"); };
    const openClone = (b) => { setFormInitial(b); setFormEditing(false); setView("create"); };
    const openEdit = (b) => { setFormInitial(b); setFormEditing(true); setView("create"); };

    if (view === "create")
        return <CreateBroadcast initial={formInitial} editing={formEditing}
            onCancel={() => setView(formEditing && detailId ? "detail" : "list")}
            onCreated={(id) => { setDetailId(id); setView("detail"); }} />;
    if (view === "detail" && detailId)
        return <BroadcastDetail id={detailId} onBack={() => setView("list")} onChanged={load} onEdit={openEdit} />;

    return (
        <div data-testid="broadcast-list" className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="font-heading text-2xl font-bold">Broadcasts</h2>
                    <p className="mt-1 text-sm text-stone2">Send an approved template to many contacts. Replies open the 24h window and hand off to your bot.</p>
                </div>
                <button data-testid="broadcast-new" onClick={openNew}
                    className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600">
                    <PlusCircle size={18} weight="fill" /> New broadcast
                </button>
            </div>

            {items === null ? (
                <p className="text-sm text-stone2"><CircleNotch size={14} className="mr-1 inline animate-spin" /> Loading…</p>
            ) : items.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-stone-300 p-12 text-center">
                    <Megaphone size={32} weight="duotone" className="mx-auto text-stone-400" />
                    <p className="mt-3 font-semibold">No broadcasts yet</p>
                    <p className="mt-1 text-sm text-stone2">Create your first broadcast to reach contacts from Salesforce or a CSV.</p>
                </div>
            ) : (
                <div className="grid gap-3">
                    {items.map((b) => {
                        const c = b.counts || {};
                        const editable = b.status === "draft" || b.status === "scheduled";
                        return (
                            <div key={b.id} data-testid={`broadcast-row-${b.id}`}
                                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-stone-200 bg-white p-5 transition-all hover:border-emerald-300 hover:shadow-sm">
                                <button onClick={() => { setDetailId(b.id); setView("detail"); }} className="min-w-0 flex-1 text-left" data-testid={`broadcast-open-${b.id}`}>
                                    <div className="flex items-center gap-2">
                                        <span className="font-heading text-lg font-bold">{b.name}</span>
                                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${STATUS_BADGE[b.status] || ""}`}>{b.status}</span>
                                        {b.ab_test && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">A/B</span>}
                                    </div>
                                    <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-stone2">
                                        <span className="inline-flex items-center gap-1"><Megaphone size={13} weight="duotone" className="text-emerald-500" /> Template <b className="text-stone-700">{b.template_name || "—"}</b></span>
                                        <span className="text-stone-300">·</span>
                                        <span className="inline-flex items-center gap-1"><Users size={13} weight="duotone" className="text-stone-400" /> {c.total || 0} recipients</span>
                                        <span className="text-stone-300">·</span>
                                        <span className="rounded-full bg-stone-100 px-2 py-0.5 font-medium text-stone-600">{SOURCE_LABEL[b.data_source?.kind] || "CSV upload"}</span>
                                    </div>
                                    <div className="mt-1 flex flex-wrap items-center gap-x-1.5 text-[11px] text-stone-400" data-testid={`broadcast-meta-${b.id}`}>
                                        {b.created_by_name && <span>Created by <span className="font-medium text-stone-500">{b.created_by_name}</span></span>}
                                        {b.created_by_name && b.created_at && <span>·</span>}
                                        {b.created_at && <span>{fmtDate(b.created_at)}</span>}
                                        {b.status === "scheduled" && b.scheduled_at && (
                                            <span className="inline-flex items-center gap-1 text-amber-600"><Clock size={12} weight="duotone" /> Scheduled {fmtDate(b.scheduled_at)}</span>
                                        )}
                                        {b.status === "completed" && b.completed_at && (
                                            <span className="inline-flex items-center gap-1"><CheckCircle size={12} weight="duotone" className="text-emerald-500" /> Completed {fmtDate(b.completed_at)}</span>
                                        )}
                                    </div>
                                </button>
                                <div className="flex items-center gap-4">
                                    <div className="hidden gap-4 text-center text-xs sm:flex">
                                        <div><div className="font-bold text-sky-600">{c.sent || 0}</div><div className="text-stone2">sent</div></div>
                                        <div><div className="font-bold text-emerald-600">{c.read || 0}</div><div className="text-stone2">read</div></div>
                                        <div><div className="font-bold text-emerald-600">{c.replied || 0}</div><div className="text-stone2">replied</div></div>
                                        <div><div className="font-bold text-red-500">{c.failed || 0}</div><div className="text-stone2">failed</div></div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        {editable && (
                                            <button data-testid={`broadcast-edit-${b.id}`} onClick={() => openEdit(b)}
                                                title="Edit broadcast" className="rounded-lg p-2 text-stone2 hover:bg-stone-100 hover:text-ink"><PencilSimple size={16} /></button>
                                        )}
                                        <button data-testid={`broadcast-clone-${b.id}`} onClick={() => openClone(b)}
                                            title="Duplicate broadcast" className="rounded-lg p-2 text-stone2 hover:bg-stone-100 hover:text-ink"><CopySimple size={16} /></button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
